import { UserRole } from '../types';

/**
 * Landing paths for the roles of the documentation module.
 *
 * DGKP (NURSE), 24h-Betreuer (CAREGIVER) and relatives (FAMILY) have no
 * dashboard access — routing them to /dashboard ends on "Zugriff verweigert".
 * Used by LoginPage, the invitation flow and the role-aware "/" redirect so all
 * three agree.
 *
 * CAREGIVER ist ein Sonderfall: die Rolle existierte vor dem Doku-Modul und
 * konnte Backoffice-Rechte tragen. Solche Benutzer landen weiterhin im
 * Dashboard — nur Betreuer ohne Dashboard-Berechtigung sind reine
 * Dokumentations-Benutzer.
 */
const hasBackofficeAccess = (permissions?: { dashboard?: boolean } | null): boolean =>
  !!permissions?.dashboard;

export const isDocumentationRole = (
  role?: string,
  permissions?: { dashboard?: boolean } | null,
): boolean => {
  if (role === UserRole.NURSE || role === UserRole.FAMILY) return true;
  if (role === UserRole.CAREGIVER) return !hasBackofficeAccess(permissions);
  return false;
};

/** Startseite des Dokumentations-Moduls. Frueher /herzensapp — die alte
 *  Adresse leitet weiter, damit bestehende Links nicht ins Leere laufen. */
export const DOCUMENTATION_PATH = '/documents';
const LEGACY_DOCUMENTATION_PATH = '/herzensapp';

/**
 * Pages a STAFF member may be sent to when the dashboard is not among their
 * rights, in the order the sidebar shows them.
 */
const STAFF_FALLBACK_PAGES: Array<{ permission: string; path: string }> = [
  { permission: 'patients', path: '/patients' },
  { permission: 'caregivers', path: '/caregivers' },
  { permission: 'shifts', path: '/shifts' },
  { permission: 'tasks', path: '/tasks' },
  { permission: 'invoices', path: '/invoices' },
  { permission: 'emails', path: '/emails' },
  { permission: 'templates', path: '/templates' },
  { permission: 'knowledge', path: '/knowledge' },
  { permission: 'files', path: '/files' },
  { permission: 'matching', path: '/matching' },
  { permission: 'staff', path: '/staff' },
  { permission: 'settings', path: '/settings' },
  { permission: 'billing', path: '/billing' },
];

const permissionGranted = (
  permissions: Record<string, any> | null | undefined,
  permission: string,
): boolean => {
  const value = permissions?.[permission];
  // "shifts" carries a level instead of a boolean.
  if (permission === 'shifts') return !!value && value !== 'none';
  return value === true;
};

export const landingPathForRole = (
  role?: string,
  permissions?: Record<string, any> | null,
): string => {
  if (isDocumentationRole(role, permissions)) return DOCUMENTATION_PATH;
  // Only STAFF can lack the dashboard; admins always have it.
  if (role === UserRole.STAFF && permissions && !permissionGranted(permissions, 'dashboard')) {
    const first = STAFF_FALLBACK_PAGES.find((page) => permissionGranted(permissions, page.permission));
    if (first) return first.path;
  }
  return '/dashboard';
};

/**
 * Where to send a user after login. `from` is the page they were redirected
 * away from; for documentation roles it is ignored unless it points into the
 * module itself, so stale links cannot dead-end them.
 */
export const resolveLoginTarget = (
  role: string | undefined,
  from?: string,
  permissions?: Record<string, any> | null,
): string => {
  if (isDocumentationRole(role, permissions)) {
    const intoModule =
      from &&
      (from.startsWith(DOCUMENTATION_PATH) || from.startsWith(LEGACY_DOCUMENTATION_PATH));
    return intoModule ? from! : DOCUMENTATION_PATH;
  }
  return from || landingPathForRole(role, permissions);
};
